import { useState, useEffect, useCallback } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/lib/auth';
import api from '@/lib/api';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  LayoutDashboard, Package, Users, Bell, LogOut, Menu, X,
  MessageSquare, Activity, User, Warehouse
} from 'lucide-react';

export const DashboardLayout = ({ children }) => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [showNotifs, setShowNotifs] = useState(false);

  const unreadCount = notifications.filter(n => !n.read).length;

  const fetchNotifications = useCallback(async () => {
    try {
      const res = await api.get('/notifications');
      setNotifications(res.data);
    } catch (e) { console.error('Failed to fetch notifications:', e.message); }
  }, []);

  useEffect(() => { fetchNotifications(); }, [fetchNotifications]);

  const markAllRead = async () => {
    try {
      await api.put('/notifications/read-all');
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
    } catch (e) { console.error('Failed to mark notifications read:', e.message); }
  };

  const isAdmin = user?.role === 'admin' || user?.role === 'super_admin';

  const navItems = {
    super_admin: [
      { icon: User, label: 'Profile', path: '/dashboard/profile' },
      { icon: LayoutDashboard, label: 'Dashboard', path: '/dashboard/admin' },
      { icon: Users, label: 'Users', path: '/dashboard/admin/users' },
      { icon: Warehouse, label: 'Floor Data', path: '/dashboard/admin/floor' },
      { icon: MessageSquare, label: 'Leads', path: '/dashboard/admin/leads' },
      { icon: Activity, label: 'Activity Logs', path: '/dashboard/admin/activity' },
    ],
    admin: [
      { icon: User, label: 'Profile', path: '/dashboard/profile' },
      { icon: LayoutDashboard, label: 'Dashboard', path: '/dashboard/admin' },
      { icon: Users, label: 'Users', path: '/dashboard/admin/users' },
      { icon: Warehouse, label: 'Floor Data', path: '/dashboard/admin/floor' },
      { icon: MessageSquare, label: 'Leads', path: '/dashboard/admin/leads' },
      { icon: Activity, label: 'Activity Logs', path: '/dashboard/admin/activity' },
    ],
    internal: [
      { icon: User, label: 'Profile', path: '/dashboard/profile' },
      { icon: LayoutDashboard, label: 'Dashboard', path: '/dashboard/internal' },
      { icon: Warehouse, label: 'Floor Data', path: '/dashboard/internal/floor' },
    ],
    merchant: [
      { icon: User, label: 'Profile', path: '/dashboard/profile' },
      { icon: Package, label: 'My Orders', path: '/dashboard/merchant' },
    ],
    buyer: [
      { icon: User, label: 'Profile', path: '/dashboard/profile' },
      { icon: Package, label: 'My Orders', path: '/dashboard/buyer' },
    ],
  };

  const items = navItems[user?.role] || [];

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const isActive = (path) => location.pathname === path;

  return (
    <div className="flex h-screen bg-[#FAFAFA]">
      {/* Desktop Sidebar */}
      <aside className="hidden lg:flex flex-col w-[240px] bg-white border-r border-black/[0.08] flex-shrink-0">
        <div className="p-6 border-b border-black/[0.05]">
          <Link to="/" className="flex items-center gap-2.5">
            <img src="/logo-icon.png" alt="Paridhi Apparels" className="h-8 w-auto" />
            <h1 className="font-heading text-xl font-semibold tracking-tight leading-tight logo-text-animated">
              Paridhi Apparels
            </h1>
            <p className="text-[9px] tracking-[0.15em] uppercase text-[#C2A878] font-semibold mt-0.5">
              Manufacturing Portal
            </p>
          </Link>
        </div>
        <nav className="flex-1 p-3 space-y-0.5">
          {items.map((item) => (
            <Link
              key={item.path}
              to={item.path}
              data-testid={`nav-${item.label.toLowerCase().replace(/\s/g, '-')}`}
              className={`sidebar-link ${isActive(item.path) ? 'active' : ''}`}
            >
              <item.icon className="w-4 h-4" />
              <span>{item.label}</span>
            </Link>
          ))}
        </nav>
        <div className="p-4 border-t border-black/[0.05]">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-md bg-[#1C1C1C] flex items-center justify-center text-white text-sm font-semibold flex-shrink-0">
              {user?.name?.[0]?.toUpperCase()}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-[#0A0A0A] truncate">{user?.name}</p>
              <p className="text-[11px] text-[#A1A1AA] capitalize">{user?.role?.replace('_', ' ')}</p>
            </div>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-h-0 min-w-0">
        <header className="glass-header sticky top-0 z-30 px-4 lg:px-8 py-3 flex items-center justify-between gap-4">
          <button
            onClick={() => setSidebarOpen(true)}
            className="lg:hidden p-2 rounded-md hover:bg-black/5 transition-colors"
            data-testid="mobile-menu-btn"
          >
            <Menu className="w-5 h-5 text-[#0A0A0A]" />
          </button>
          <div className="lg:hidden flex items-center gap-2">
            <img src="/logo-icon.png" alt="Paridhi Apparels" className="h-6 w-auto" />
            <span className="font-heading text-lg font-semibold logo-text-animated">Paridhi Apparels</span>
          </div>
          <div className="flex-1" />
          <div className="flex items-center gap-2">
            <div className="relative">
              <button
                onClick={() => setShowNotifs(!showNotifs)}
                className="relative p-2 rounded-md hover:bg-black/5 transition-colors"
                data-testid="notifications-btn"
              >
                <Bell className="w-5 h-5 text-[#52525B]" />
                {unreadCount > 0 && (
                  <span className="notification-dot">{unreadCount > 9 ? '9+' : unreadCount}</span>
                )}
              </button>
              {showNotifs && (
                <>
                  <div className="fixed inset-0 z-40" onClick={() => setShowNotifs(false)} />
                  <div className="absolute right-0 top-full mt-2 w-80 bg-white rounded-md border border-black/[0.08] shadow-lg z-50 overflow-hidden" data-testid="notifications-panel">
                    <div className="px-4 py-3 border-b border-black/[0.05] flex items-center justify-between">
                      <p className="text-sm font-semibold text-[#0A0A0A]">Notifications</p>
                      {unreadCount > 0 && (
                        <button onClick={markAllRead} className="text-xs text-[#C2A878] font-medium hover:underline" data-testid="mark-all-read-btn">
                          Mark all read
                        </button>
                      )}
                    </div>
                    <ScrollArea className="max-h-[320px]">
                      {notifications.length === 0 ? (
                        <p className="p-4 text-sm text-[#A1A1AA] text-center">No notifications</p>
                      ) : (
                        notifications.slice(0, 10).map((n) => (
                          <div
                            key={n.id}
                            className={`px-4 py-3 border-b border-black/[0.03] ${!n.read ? 'bg-amber-50/50' : ''}`}
                            onClick={() => {
                              if (n.order_id) {
                                const prefix = user.role === 'buyer' ? '/dashboard/buyer' : user.role === 'internal' ? '/dashboard/internal' : user.role === 'merchant' ? '/dashboard/merchant' : '/dashboard/admin';
                                navigate(`${prefix}/orders/${n.order_id}`);
                                setShowNotifs(false);
                              }
                            }}
                            style={{ cursor: n.order_id ? 'pointer' : 'default' }}
                          >
                            <p className="text-sm text-[#0A0A0A] leading-relaxed">{n.message}</p>
                            <p className="text-[11px] text-[#A1A1AA] mt-1">
                              {new Date(n.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                            </p>
                          </div>
                        ))
                      )}
                    </ScrollArea>
                  </div>
                </>
              )}
            </div>
            <button
              onClick={handleLogout}
              className="p-2 rounded-md hover:bg-black/5 transition-colors"
              data-testid="logout-btn"
            >
              <LogOut className="w-5 h-5 text-[#52525B]" />
            </button>
          </div>
        </header>
        <main className="flex-1 overflow-y-auto p-4 lg:p-8">
          {children}
        </main>
      </div>

      {/* Mobile Sidebar */}
      {sidebarOpen && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div className="absolute inset-0 bg-black/40" onClick={() => setSidebarOpen(false)} />
          <aside className="absolute left-0 top-0 bottom-0 w-72 bg-white shadow-2xl flex flex-col animate-slide-in-right">
            <div className="p-6 border-b border-black/[0.05] flex items-center justify-between">
              <div>
                <h1 className="font-heading text-xl font-semibold text-[#1C1C1C]">Paridhi Apparels</h1>
                <p className="text-[10px] tracking-[0.2em] uppercase text-[#C2A878] font-semibold">Portal</p>
              </div>
              <button onClick={() => setSidebarOpen(false)} data-testid="close-sidebar-btn">
                <X className="w-5 h-5" />
              </button>
            </div>
            <nav className="flex-1 p-3 space-y-0.5">
              {items.map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={() => setSidebarOpen(false)}
                  className={`sidebar-link ${isActive(item.path) ? 'active' : ''}`}
                >
                  <item.icon className="w-4 h-4" />
                  <span>{item.label}</span>
                </Link>
              ))}
            </nav>
            <div className="p-4 border-t border-black/[0.05]">
              <button onClick={handleLogout} className="sidebar-link w-full text-red-500 hover:bg-red-50" data-testid="mobile-logout-btn">
                <LogOut className="w-4 h-4" />
                <span>Sign Out</span>
              </button>
            </div>
          </aside>
        </div>
      )}
    </div>
  );
};
